import { Icon as IconifyIcon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Alert, Switch as MantineSwitch, TextInput } from '@mantine/core';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ApprovalMode } from '../../../../api/agents-client/agent-client.type';
import {
  useAutomationFilterDefinitions,
  useAutomationWorkflow,
  useCreateAutomationWorkflow,
  useDeleteAutomationWorkflow,
  useUpdateAutomationWorkflow,
} from '../../../../api/automations-workflows-client/automations-workflows-client.hooks';
import {
  AutomationActionType,
  type AutomationActionConfig,
  AutomationEventType,
  AutomationTriggerType,
  type AutomationWorkflowWrite,
  type DeliveryChannel,
  type FilterRules,
} from '../../../../api/automations-workflows-client/automations-workflows-client.type';
import { formatPromptWithMentions } from '../../../../containers/flora/components/flora-mention/flora-mention.helpers';
import type { SelectedMention } from '../../../../containers/flora/components/flora-mention/flora-mention.type';
import { toast } from '../../../../containers/notifications';
import { UserContext } from '../../../../contexts/user';
import { useResizableWidth } from '../../../../hooks/useResizableWidth';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { baseWhite, secondaryBase } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Loader } from '../../../../ui-library/loader/loader';
import { Text } from '../../../../ui-library/typography/typography';
import { AgentAutomationActionConfig } from './agent-automation-action-config';
import { AgentAutomationDeliveryConfig } from './agent-automation-delivery-config';
import { AgentAutomationStepCard, AgentAutomationStepConnector, type StepKind } from './agent-automation-step-card';
import { AgentAutomationTriggerConfig } from './agent-automation-trigger-config';
import {
  emptyFilterRules,
  getActionLabel,
  getActionStepVisual,
  getDeliveryStepVisual,
  getEventLabel,
  getTriggerStepVisual,
  getUnsatisfiedRequiredFilters,
  normalizeFilterRules,
  seedRequiredFilterRules,
  serializeFilterRules,
  summarizeDeliveryChannels,
} from './agent-automations.helpers';

const MIN_CONFIG_WIDTH = 360;
const DEFAULT_CONFIG_WIDTH = 600;

interface AgentAutomationBuilderProps {
  workflowId: string | null;
  onClose: () => void;
  // Invoked after a successful delete. Required (not folded into `onClose`)
  // because a delete must route away from the now-removed workflow's detail
  // page, which would otherwise 404.
  onDeleted: () => void;
}

export function AgentAutomationBuilder({ workflowId, onClose, onDeleted }: AgentAutomationBuilderProps) {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const { user } = useContext(UserContext);
  const userEmail = user?.email ?? '';
  const userId = user?.id ?? null;

  const { workflow, isLoading: workflowLoading } = useAutomationWorkflow(workflowId ?? undefined);
  const { definitions, isLoading: definitionsLoading } = useAutomationFilterDefinitions();
  const createMutation = useCreateAutomationWorkflow();
  const updateMutation = useUpdateAutomationWorkflow();
  const deleteMutation = useDeleteAutomationWorkflow();

  const isEditing = !!workflowId;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [triggerType, setTriggerType] = useState<AutomationTriggerType>(AutomationTriggerType.EVENT);
  const [eventType, setEventType] = useState<AutomationEventType | null>(null);
  const [filterRules, setFilterRules] = useState<FilterRules>(emptyFilterRules());
  const [cronInitialValue, setCronInitialValue] = useState<string | null>(null);
  const [cronExpression, setCronExpression] = useState<string | null>(null);
  const [cronValid, setCronValid] = useState(false);
  const [actionType, setActionType] = useState<AutomationActionType>(AutomationActionType.FLORA_PROMPT);
  const [floraPrompt, setFloraPrompt] = useState('');
  const [floraPromptMentions, setFloraPromptMentions] = useState<SelectedMention[]>([]);
  const [staticMessage, setStaticMessage] = useState('');
  const [approvalMode, setApprovalMode] = useState<ApprovalMode | null>(null);
  const [deliveryInitial, setDeliveryInitial] = useState<DeliveryChannel[]>([]);
  const [deliveryChannels, setDeliveryChannels] = useState<DeliveryChannel[]>([]);
  const [deliveryValid, setDeliveryValid] = useState(false);
  const [selectedStep, setSelectedStep] = useState<StepKind>('trigger');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [hasHydrated, setHasHydrated] = useState(false);

  const builderBodyRef = useRef<HTMLDivElement>(null);
  const {
    width: configWidth,
    isResizing: isResizingConfig,
    handleMouseDown: startConfigResize,
  } = useResizableWidth({
    initialWidth: DEFAULT_CONFIG_WIDTH,
    minWidth: MIN_CONFIG_WIDTH,
    maxWidth: () =>
      builderBodyRef.current
        ? Math.max(MIN_CONFIG_WIDTH, builderBodyRef.current.getBoundingClientRect().width * 0.7)
        : DEFAULT_CONFIG_WIDTH,
    computeWidth: (event) => {
      const containerRect = builderBodyRef.current?.getBoundingClientRect();
      return containerRect ? containerRect.right - event.clientX : DEFAULT_CONFIG_WIDTH;
    },
  });

  useEffect(() => {
    if (!isEditing) {
      setHasHydrated(true);
      return;
    }
    if (workflowLoading) return;
    setHasHydrated(true);
    if (!workflow) return;
    setName(workflow.name);
    setDescription(workflow.description ?? '');
    setIsActive(workflow.is_active);
    setTriggerType(workflow.trigger_type);
    setEventType(workflow.event_type);
    setFilterRules(normalizeFilterRules(workflow.filter_rules));
    setCronInitialValue(workflow.cron_expression ?? null);
    setCronExpression(workflow.cron_expression ?? null);
    setActionType(workflow.action_type);
    const config = workflow.action_config as AutomationActionConfig;
    if (workflow.action_type === AutomationActionType.FLORA_PROMPT) {
      const prompt = (config as { prompt?: unknown }).prompt;
      setFloraPrompt(typeof prompt === 'string' ? prompt : '');
      setStaticMessage('');
    } else {
      const message = (config as { message?: unknown }).message;
      setStaticMessage(typeof message === 'string' ? message : '');
      setFloraPrompt('');
    }
    // Persisted prompts are already substituted (no `[@Name]` placeholders),
    // so editing a saved workflow starts with an empty mentions list.
    setFloraPromptMentions([]);
    setApprovalMode(workflow.approval_mode ?? null);
    setDeliveryInitial(workflow.delivery_channels);
    setDeliveryChannels(workflow.delivery_channels);
    setHasHydrated(true);
  }, [isEditing, workflowLoading, workflow]);

  const eventDefinition = useMemo(
    () => definitions.find((definition) => definition.event_type === eventType) ?? null,
    [definitions, eventType],
  );

  const unsatisfiedRequiredFilters = useMemo<string[]>(
    () => getUnsatisfiedRequiredFilters(eventDefinition, filterRules.rules),
    [eventDefinition, filterRules.rules],
  );

  const eventRequiredFiltersSatisfied = eventDefinition !== null && unsatisfiedRequiredFilters.length === 0;

  const triggerConfigured =
    triggerType === AutomationTriggerType.EVENT
      ? !!eventType && eventRequiredFiltersSatisfied
      : triggerType === AutomationTriggerType.CRON
        ? cronValid
        : true;
  const actionConfigured =
    actionType === AutomationActionType.FLORA_PROMPT ? floraPrompt.trim().length > 0 : staticMessage.trim().length > 0;
  // Delivery is optional, so an empty list is valid for saving. Keep it
  // visually unconfigured, however, so the green complete state remains a
  // clear signal that the user chose and completed a delivery destination.
  const deliveryConfigured = deliveryValid && deliveryChannels.length > 0;

  const triggerVisual = getTriggerStepVisual(triggerType);
  const actionVisual = getActionStepVisual(actionType);
  const deliveryVisual = getDeliveryStepVisual();

  const triggerSubtitle = useMemo<string | null>(() => {
    if (triggerType === AutomationTriggerType.MANUAL) {
      return 'Fires only when triggered manually.';
    }
    if (triggerType === AutomationTriggerType.CRON) {
      if (!cronExpression) return 'Pick a recurring schedule.';
      return `On schedule (${cronExpression})`;
    }
    if (!eventType) return 'Pick a Bloomfilter event that starts this flow.';
    if (unsatisfiedRequiredFilters.length > 0) {
      const noun = unsatisfiedRequiredFilters.length === 1 ? 'filter' : 'filters';
      return `Add required ${noun}: ${unsatisfiedRequiredFilters.join(', ')}.`;
    }
    const ruleCount = filterRules.rules.length;
    if (ruleCount === 0) return 'Fires on every event of this type.';
    return `${ruleCount} filter ${ruleCount === 1 ? 'rule' : 'rules'} (${filterRules.mode}).`;
  }, [triggerType, cronExpression, eventType, filterRules, unsatisfiedRequiredFilters]);

  const actionSubtitle = useMemo<string | null>(() => {
    if (!actionConfigured) return 'Choose Ask Flora or write a static message.';
    if (actionType === AutomationActionType.FLORA_PROMPT) {
      const preview = floraPrompt.trim();
      return preview.length > 80 ? `${preview.slice(0, 80)}…` : preview;
    }
    const preview = staticMessage.trim();
    return preview.length > 80 ? `${preview.slice(0, 80)}…` : preview;
  }, [actionConfigured, actionType, floraPrompt, staticMessage]);

  const deliverySubtitle = useMemo<string | null>(() => {
    if (deliveryChannels.length === 0) {
      return deliveryValid
        ? 'Optional — results remain available in run history.'
        : 'Complete the selected delivery destination.';
    }
    return summarizeDeliveryChannels(deliveryChannels);
  }, [deliveryChannels, deliveryValid]);

  const isValid = name.trim().length > 0 && triggerConfigured && actionConfigured && deliveryValid;

  const buildPayload = useCallback((): AutomationWorkflowWrite | null => {
    if (!organizationId) return null;
    if (triggerType === AutomationTriggerType.EVENT && !eventType) return null;
    if (triggerType === AutomationTriggerType.CRON && !cronExpression) return null;
    const actionConfig: AutomationActionConfig =
      actionType === AutomationActionType.FLORA_PROMPT
        ? { prompt: formatPromptWithMentions(floraPrompt, floraPromptMentions).trim() }
        : { message: staticMessage.trim() };
    return {
      organization: organizationId,
      name: name.trim(),
      description: description.trim() || null,
      is_active: isActive,
      trigger_type: triggerType,
      cron_expression: triggerType === AutomationTriggerType.CRON ? cronExpression : null,
      event_type: triggerType === AutomationTriggerType.EVENT ? eventType : null,
      filter_rules: triggerType === AutomationTriggerType.EVENT ? serializeFilterRules(filterRules) : {},
      action_type: actionType,
      action_config: actionConfig,
      delivery_channels: deliveryChannels,
      approval_mode: approvalMode,
    };
  }, [
    organizationId,
    name,
    description,
    isActive,
    triggerType,
    cronExpression,
    eventType,
    filterRules,
    actionType,
    floraPrompt,
    floraPromptMentions,
    staticMessage,
    deliveryChannels,
    approvalMode,
  ]);

  const handleSave = useCallback((): void => {
    const payload = buildPayload();
    if (!payload) {
      toast.error('Organization context is unavailable. Please retry.');
      return;
    }
    setServerError(null);
    if (isEditing && workflowId) {
      updateMutation.mutate(
        { id: workflowId, data: payload },
        {
          onSuccess: () => {
            toast.success('Automation updated');
            onClose();
          },
          onError: (error) => {
            setServerError(error.message);
            toast.error('Failed to update automation');
          },
        },
      );
      return;
    }
    createMutation.mutate(payload, {
      onSuccess: () => {
        toast.success('Automation created');
        onClose();
      },
      onError: (error) => {
        setServerError(error.message);
        toast.error('Failed to create automation');
      },
    });
  }, [buildPayload, isEditing, workflowId, updateMutation, createMutation, onClose]);

  const handleDelete = useCallback((): void => {
    if (!workflowId) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    deleteMutation.mutate(workflowId, {
      onSuccess: () => {
        toast.success('Automation deleted');
        onDeleted();
      },
      onError: () => toast.error('Failed to delete automation'),
    });
  }, [workflowId, confirmDelete, deleteMutation, onDeleted]);

  if (isEditing && workflowLoading) {
    return (
      <LoadingContainer>
        <Loader />
      </LoadingContainer>
    );
  }

  if (isEditing && !workflow && hasHydrated) {
    return (
      <LoadingContainer>
        <Alert color="red" variant="light" icon={<Icon name="error" color="#d3180c" size={18} />}>
          <Text size="small" color="var(--ink-dark)">
            Automation not found.
          </Text>
        </Alert>
      </LoadingContainer>
    );
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isDeleting = deleteMutation.isPending;

  return (
    <Shell>
      <TopBar>
        <TopBarLeft>
          <BackButton type="button" onClick={onClose}>
            <Icon name="arrow_back" color="var(--ink-dark)" size={16} />
            Back
          </BackButton>
          <TitleStack>
            <TitleInput
              value={name}
              placeholder="Untitled automation"
              onChange={(event) => setName(event.currentTarget.value)}
              aria-label="Automation name"
              styles={{
                input: {
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  fontWeight: 700,
                  fontSize: 18,
                  color: 'var(--ink-darkest)',
                },
              }}
              variant="unstyled"
            />
            <DescriptionInput
              value={description}
              placeholder="Describe what this automation does…"
              onChange={(event) => setDescription(event.currentTarget.value)}
              aria-label="Automation description"
              styles={{
                input: {
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  fontSize: 13,
                  color: 'var(--ink-base)',
                },
              }}
              variant="unstyled"
            />
          </TitleStack>
        </TopBarLeft>
        <TopBarRight>
          <ActiveSwitchWrapper>
            <Switch
              checked={isActive}
              onChange={(event) => setIsActive(event.currentTarget.checked)}
              aria-label="Active"
            />
            <Text size="small" weight="medium" color="var(--ink-dark)">
              {isActive ? 'Active' : 'Paused'}
            </Text>
          </ActiveSwitchWrapper>
          {isEditing && (
            <DeleteButton type="button" onClick={handleDelete} disabled={isDeleting}>
              {confirmDelete ? 'Confirm delete?' : 'Delete'}
            </DeleteButton>
          )}
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} loading={isSaving} disabled={!isValid}>
            {isEditing ? 'Save Changes' : 'Create Automation'}
          </Button>
        </TopBarRight>
      </TopBar>

      {serverError && (
        <ServerErrorBar>
          <Alert color="red" variant="light" icon={<Icon name="error" color="#d3180c" size={18} />}>
            <Text size="small" color="var(--ink-dark)">
              {serverError}
            </Text>
          </Alert>
        </ServerErrorBar>
      )}

      <BuilderBody ref={builderBodyRef}>
        <Canvas>
          <CanvasInner>
            <AgentAutomationStepCard
              stepNumber={1}
              kind="trigger"
              kindLabel="When this happens"
              title={
                triggerType === AutomationTriggerType.MANUAL
                  ? 'Manual trigger'
                  : triggerType === AutomationTriggerType.CRON
                    ? 'On a schedule'
                    : eventType
                      ? getEventLabel(eventType)
                      : 'Choose a trigger'
              }
              subtitle={triggerSubtitle}
              iconName={triggerVisual.icon}
              iconColor={triggerVisual.iconColor}
              iconBackground={triggerVisual.iconBackground}
              configured={triggerConfigured}
              selected={selectedStep === 'trigger'}
              onSelect={() => setSelectedStep('trigger')}
            />
            <AgentAutomationStepConnector />
            <AgentAutomationStepCard
              stepNumber={2}
              kind="action"
              kindLabel="Do this"
              title={getActionLabel(actionType)}
              subtitle={actionSubtitle}
              iconName={actionVisual.icon}
              iconColor={actionVisual.iconColor}
              iconBackground={actionVisual.iconBackground}
              configured={actionConfigured}
              selected={selectedStep === 'action'}
              onSelect={() => setSelectedStep('action')}
            />
            <AgentAutomationStepConnector />
            <AgentAutomationStepCard
              stepNumber={3}
              kind="delivery"
              kindLabel="Deliver to"
              title={
                deliveryChannels.length > 0
                  ? summarizeDeliveryChannels(deliveryChannels)
                  : deliveryValid
                    ? 'No delivery'
                    : 'Delivery incomplete'
              }
              subtitle={deliverySubtitle}
              iconName={deliveryVisual.icon}
              iconColor={deliveryVisual.iconColor}
              iconBackground={deliveryVisual.iconBackground}
              configured={deliveryConfigured}
              selected={selectedStep === 'delivery'}
              onSelect={() => setSelectedStep('delivery')}
            />
          </CanvasInner>
        </Canvas>

        <ConfigDividerHandle onMouseDown={startConfigResize} data-dragging={isResizingConfig}>
          <ConfigDragDots>
            <IconifyIcon icon="material-symbols:drag-indicator" width={14} height={14} />
          </ConfigDragDots>
        </ConfigDividerHandle>

        <ConfigColumn style={{ width: configWidth, minWidth: configWidth }}>
          <ConfigHeader>
            <Text size="medium" weight="bold" color="var(--ink-darkest)">
              {selectedStep === 'trigger' && 'When this happens'}
              {selectedStep === 'action' && 'Do this'}
              {selectedStep === 'delivery' && 'Deliver to'}
            </Text>
            <Text size="small" color="var(--ink-base)">
              {selectedStep === 'trigger' && 'Pick a Bloomfilter event and optionally narrow it down with filters.'}
              {selectedStep === 'action' && 'Take an action when the trigger fires.'}
              {selectedStep === 'delivery' && 'Optionally send the result to email, Slack, or Teams.'}
            </Text>
          </ConfigHeader>
          <ConfigBody>
            <ConfigStep $visible={selectedStep === 'trigger'}>
              <AgentAutomationTriggerConfig
                triggerType={triggerType}
                eventType={eventType}
                filterRules={filterRules}
                cronInitialValue={cronInitialValue}
                definitions={definitions}
                definitionsLoading={definitionsLoading}
                onChangeTriggerType={(nextType) => {
                  setTriggerType(nextType);
                  if (nextType !== AutomationTriggerType.EVENT) {
                    setEventType(null);
                    setFilterRules(emptyFilterRules());
                  }
                  if (nextType !== AutomationTriggerType.CRON) {
                    setCronExpression(null);
                    setCronValid(false);
                  }
                }}
                onChangeEventType={(next) => {
                  setEventType(next);
                  // Seeding relies on the Event select being disabled until
                  // definitions load, so `next` always resolves to a definition.
                  setFilterRules(seedRequiredFilterRules(next, definitions));
                }}
                onChangeFilterRules={setFilterRules}
                onChangeCron={setCronExpression}
                onChangeCronValidity={setCronValid}
              />
            </ConfigStep>
            <ConfigStep $visible={selectedStep === 'action'}>
              <AgentAutomationActionConfig
                key={workflowId ?? 'new'}
                actionType={actionType}
                floraPrompt={floraPrompt}
                staticMessage={staticMessage}
                eventDefinition={eventDefinition}
                onChangeActionType={setActionType}
                onChangeFloraPrompt={setFloraPrompt}
                onChangeStaticMessage={setStaticMessage}
                onChangeFloraMentions={setFloraPromptMentions}
                approvalMode={approvalMode}
                onChangeApprovalMode={setApprovalMode}
              />
            </ConfigStep>
            <ConfigStep $visible={selectedStep === 'delivery'}>
              <AgentAutomationDeliveryConfig
                initialChannels={deliveryInitial}
                userEmail={userEmail}
                userId={userId}
                onChange={setDeliveryChannels}
                onValidityChange={setDeliveryValid}
              />
            </ConfigStep>
          </ConfigBody>
        </ConfigColumn>
      </BuilderBody>
    </Shell>
  );
}

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: var(--sky-lightest);
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 32px;
  border-bottom: 1px solid var(--sky-light);
  background: var(--base-white);
  flex-shrink: 0;
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: 1px solid var(--sky-light);
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-dark);
  transition: background 0.12s, border-color 0.12s;

  &:hover {
    background: var(--sky-lighter);
    border-color: var(--sky-base);
  }
`;

const TitleStack = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

const TitleInput = styled(TextInput)`
  width: 100%;
`;

const DescriptionInput = styled(TextInput)`
  width: 100%;
  margin-top: 2px;
`;

const ActiveSwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Switch = styled(MantineSwitch)`
  input:checked + .mantine-Switch-track {
    border-color: ${secondaryBase};
    background: ${secondaryBase};

    .mantine-Switch-thumb {
      background: ${baseWhite};
    }
  }
`;

const DeleteButton = styled.button`
  background: transparent;
  border: none;
  color: var(--red-base, #ff5247);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 8px;
  transition: background 0.12s;

  &:hover:not(:disabled) {
    background: rgba(255, 82, 71, 0.08);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const ServerErrorBar = styled.div`
  padding: 12px 32px 0;
`;

const BuilderBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

const Canvas = styled.div`
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: 40px 24px 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CanvasInner = styled.div`
  width: 100%;
  max-width: 620px;
  display: flex;
  flex-direction: column;
`;

const ConfigDividerHandle = styled.div`
  width: 5px;
  background: var(--sky-light);
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: col-resize;
  transition: background 0.15s;

  &::before {
    content: '';
    position: absolute;
    inset: 0 -3px;
  }

  &:hover,
  &[data-dragging='true'] {
    background: var(--secondary-light);
  }

  &:hover > span,
  &[data-dragging='true'] > span {
    border-color: var(--secondary-light);
    color: var(--secondary-base);
  }
`;

const ConfigDragDots = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ink-lighter);
  background: var(--base-white);
  border: 1px solid var(--sky-light);
  border-radius: 4px;
  padding: 2px 0;
  pointer-events: none;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
`;

const ConfigColumn = styled.div`
  flex-shrink: 0;
  background: var(--base-white);
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ConfigHeader = styled.div`
  padding: 20px 24px 14px;
  border-bottom: 1px solid var(--sky-light);
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ConfigBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 22px 24px 60px;
`;

const ConfigStep = styled.div<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? 'block' : 'none')};
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 60px;
`;

export type { AgentAutomationBuilderProps };
